import { FC } from 'react'
import dayjs from 'dayjs'
import Head from 'next/head'
import Image from 'next/image'
import classNames from 'classnames'
import parse from 'html-react-parser'
import { useTranslation } from 'react-i18next'
import styles from './EncyclopediaArticle.module.scss'
import bodyStyles from 'components/article/article-body/ArticleBody.module.scss'
import { ContentBlocks, EncyclopediaArticleProps as Props } from '../types'
import EncyclopediaNav from 'features/encyclopedia/components/encyclopedia-nav/EncyclopediaNav'
import { ArticleBody, ArticleFooter, ArticleHeader, ArticleIntro, ArticleValve } from 'components/article'
import ContactCTA from 'components/contact-cta/ContactCTA'
import ArticleSection from 'features/encyclopedia/components/article-section/ArticleSection'
import { TITLE_POSTFIX } from 'constants/seo'
import { generateArticleStructuredData } from 'utils/structuredData'
import { useRouter } from 'next/router'

const EncyclopediaArticle: FC<Props> = ({ allArticles, articleContent }) => {
  // i18n
  const { t } = useTranslation('encyclopedia')

  // Next router
  const { asPath, locale } = useRouter()

  /**
   * calculates the font-size based on the amount of characters
   *
   * @param {string} quote
   * @returns string
   */
  const getQuoteFontSize = (quote: string): string => {
    if (quote?.length > 200) {
      return '20px'
    }

    if (quote?.length < 50) {
      return '32px'
    }

    return '28px'
  }

  return (
    <>
      {/* SEO */}
      <Head>
        <title>
          {articleContent?.seo?.seoTitle ?? articleContent?.title} {TITLE_POSTFIX}
        </title>
        <meta property="og:title" content={articleContent?.seo?.seoTitle ?? articleContent?.title} key="title" />
        {articleContent?.thumbnail && <meta property="og:image" content={articleContent?.thumbnail} key="image" />}
        <meta name="description" content={articleContent?.seo?.metaDescription ?? articleContent?.description} />
        <script type="application/ld+json" key="article-jsonld" dangerouslySetInnerHTML={generateArticleStructuredData(articleContent)} />
        <link rel="canonical" href={`https://www.boltenergie.be/${locale}${asPath}`} />
      </Head>

      {/* BREADCRUMBS + SEARCH */}
      <EncyclopediaNav articles={allArticles} />

      {/* CONTENT */}
      <section className={classNames('container', styles.article)}>
        {/* HEADER */}
        <ArticleHeader
          author={{
            name: articleContent.author?.firstName,
            date: dayjs(articleContent.publishedAt).format('DD/MM/YYYY'),
            image: articleContent.author?.avatar
          }}
          tag={articleContent.category.title}
          title={articleContent.title}
          tagColor={articleContent.category.color}
        />

        {/* INTRO */}
        <ArticleIntro>{parse(articleContent.description)}</ArticleIntro>

        {/* BODY */}
        <ArticleBody>
          {articleContent.contentBlocks.map((block, idx) => {
            switch (block.type) {
              case ContentBlocks.RICH_TEXT:
                return (
                  <div className={bodyStyles['rich-text']} key={`${block.type}-${idx}`}>
                    {parse(block.body)}
                  </div>
                )
              case ContentBlocks.IMAGE:
                return (
                  <figure className={bodyStyles.image} key={`${block.type}-${idx}`}>
                    <Image
                      src={block?.image?.url}
                      alt={block?.image?.alternativeText}
                      width={block?.image?.width}
                      height={block?.image?.height}
                      objectFit="cover"
                    />
                  </figure>
                )
              case ContentBlocks.GOOD_TO_KNOW:
                return (
                  <div key={`${block.type}-${idx}`} className={bodyStyles['good-to-know']}>
                    <h4 className={bodyStyles.title}>{t('goodToKnow', 'Goed om weten!')}</h4>
                    {parse(block.information)}
                  </div>
                )
              case ContentBlocks.QUOTE:
                return (
                  <figure key={`${block.type}-${idx}`} className={bodyStyles['quote-wrapper']}>
                    <blockquote className={bodyStyles.quote} style={{ fontSize: getQuoteFontSize(block?.quote) }}>
                      &quot;{block?.quote}&quot;
                    </blockquote>
                    {block?.author && <figcaption>- {block.author}</figcaption>}
                  </figure>
                )
              default:
                return null
            }
          })}
        </ArticleBody>

        {/* VALVE (afsluiter) */}
        <ArticleValve>
          {t(
            'articleValve',
            'Bolt helpt jou wegwijs maken in energie. Inzicht in je energie-aansluiting en energie algemeen helpen jou om de juiste keuzes te maken richting een klimaatneutrale toekomst.'
          )}
        </ArticleValve>

        {/* FOOTER */}
        <ArticleFooter />
      </section>

      {/* AUTHOR */}
      {articleContent?.author && (
        <ContactCTA
          avatar={articleContent.author.avatar}
          className={styles['author-contact']}
          description={articleContent.author?.personalizedFooter?.description ?? t('articleFooterDescription', 'Lorem ipsum')}
          title={
            articleContent.author?.personalizedFooter?.title ??
            t('articleFooterGreeting', 'Hallo, ik ben {{ name }}', { name: articleContent.author.firstName })
          }
        />
      )}

      {/* MORE ARTICLES */}
      <ArticleSection limit={4} randomize title={t('readMore', 'Meer lezen?')} articles={allArticles} className={styles['more-articles']} />
    </>
  )
}

export default EncyclopediaArticle
